import map from 'lodash/map';
import type { RouteRecordRaw } from 'vue-router';

import { webreportsConfiguration } from '@/features/Sparten';
import type {
  SpartenConfig,
  SpartenConfigModules,
} from '@/features/Sparten/types';
import {
  guards,
  versicherungsangabenGuards,
  webreportGuards,
} from '@/router/guards';
import { applyKfzV2Switch } from '@/router/spartenRoutesFactory/applyKfzV2Switch';
import parseSpartenConfig from '@/router/spartenRoutesFactory/parseSpartenConfig';

/**
 * Factory function to create all needed routes for all existing Sparten.
 * Iterates over the SpartenConfigModul to build all routes.
 *
 * @param modules SpartenConfigModul - Object containing all Sparten config objects
 * @returns routes - An array of RouteRecordRaw objects
 */
export const createSpartenRoutes = (
  modules: SpartenConfigModules,
): RouteRecordRaw[] => {
  // Entscheidet pro KFZ-Sparte, ob V1, V2 oder beide geroutet werden (siehe applyKfzV2Switch).
  const { modules: switchedModules, aliasRedirects } =
    applyKfzV2Switch(modules);

  const routes: RouteRecordRaw[] = [...aliasRedirects];

  map(switchedModules, (spartenConfig: SpartenConfig) => {
    const route: RouteRecordRaw = {
      ...spartenConfig,
      beforeEnter: guards.beforeEnterModuleGuard(spartenConfig),
      children: [
        {
          // need the regex to not match the actual root route /
          // fixes wrong redirect from / to first registered vorgaben page
          path: '/:pathMatch(.*)*',
          redirect: `${spartenConfig.path}/vorgaben`,
        },
        ...parseSpartenConfig(spartenConfig),
      ],
    };

    routes.push(route);
  });

  // Öffentliche Routen (Webreports & Versicherungsangaben)
  Object.entries(switchedModules).forEach(([sparte, spartenConfig]) => {
    webreportsConfiguration.forEach((config) => {
      const webreportRoute: RouteRecordRaw = {
        name: `${sparte}.webreport-${config.type}`,
        path: `${spartenConfig.path}/webreports/${config.urlPath}/:webreportId`,
        component: config.component,
        meta: {
          type: config.type,
          isWebreport: true,
        },
        props: spartenConfig.props,
        beforeEnter: webreportGuards.beforeEnterModuleGuard(
          spartenConfig,
          config.type,
        ),
      };

      routes.push(webreportRoute);
    });

    if (spartenConfig.general.versicherungsangabenConfig !== undefined) {
      const versicherungsangabenRoute: RouteRecordRaw = {
        name: `${sparte}.versicherungsangaben`,
        path: `${spartenConfig.path}/versicherungsangaben`,
        props: (route) => ({
          ...spartenConfig.props,
          quoteId: route.params.quoteId,
        }),
        component: () =>
          import(
            '@/views/Layouts/Versicherungsangaben/VersicherungsangabenLayout.vue'
          ),
        beforeEnter:
          versicherungsangabenGuards.beforeEnterModuleGuard(spartenConfig),

        children: [
          {
            name: `${sparte}.versicherungsangaben.notFound`,
            path: '404',
            component: () =>
              import(
                '@/views/Layouts/Versicherungsangaben/VersicherungsangabenNotFound.vue'
              ),
          },
          {
            name: `${sparte}.versicherungsangaben.success`,
            path: 'abschluss',
            component: () =>
              import(
                '@/views/Layouts/Versicherungsangaben/VersicherungsangabenAbschluss.vue'
              ),
          },
          {
            name: `${sparte}.versicherungsangaben.show`,
            path: ':quoteId',
            component: () =>
              import(
                '@/views/Layouts/Versicherungsangaben/VersicherungsangabenInput.vue'
              ),
            meta: {
              isVersicherungsangaben: true,
            },
          },
        ],
      };

      routes.push(versicherungsangabenRoute);
    }
  });

  return routes;
};
